import Typography from "@mui/material/Typography";
import { Alert, Button, IconButton, Skeleton, Stack } from "@mui/material";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import useApiHook from "../hooks/useApiHook";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import moment from "moment/moment";
import Transcription from "./Transcription";
import { useNotification } from "../store/notification-context";
import { useDeleteFileModal, useRenameFileModal } from "../store/modal-context";
import useAppView from "./platforms/useAppView";
import useDocumentVisibility from "../hooks/useDocumentVisibility";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';

moment.locale('it');

const FileDetails = () => {
    const { response, makeRequest, error } = useApiHook();
    const { fileId } = useParams();
    const navigate = useNavigate();
    const { notifyTranscriptionCompleted } = useNotification();
    const renameFileModal = useRenameFileModal();
    const deleteFileModal = useDeleteFileModal();
    const isDocumentVisible = useDocumentVisibility();

    const [fileDetails, setFileDetails] = useState(null);
    const [transcriptions, setTranscriptions] = useState([]);

    const pollingIntervalRef = useRef(null);
    const previousStatusRef = useRef(null);
    const backgroundedDuringPollingRef = useRef(false);
    const { isAppView } = useAppView();

    const deleteBtnClickHandler = () => {
        deleteFileModal.openModal({
            fileId,
            fileName: fileDetails?.name,
            onSuccess: () => {
                navigate('/');
            }
        });
    };

    const renameBtnClickHandler = () => {
        if (!fileDetails) {
            return;
        }

        renameFileModal.openModal({
            fileId,
            fileName: fileDetails.name,
            onSuccess: (updatedFile) => {
                setFileDetails((currentValue) => ({
                    ...currentValue,
                    ...(updatedFile || {}),
                    formattedDate: currentValue?.formattedDate
                }));
            }
        });
    };

    const analyzeWithAIClickHandler = () => {
        if (!fileDetails?.folderId) {
            return;
        }

        navigate({
            pathname: `/patient/${fileDetails.folderId}/assistant`,
            search: createSearchParams({ fileId: String(fileDetails.id) }).toString()
        });
    };

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    const startPolling = useCallback(() => {
        stopPolling();
        pollingIntervalRef.current = setInterval(() => {
            makeRequest(`/file/${fileId}/details`);
        }, 3000);
    }, [fileId, makeRequest, stopPolling]);

    useEffect(() => {
        makeRequest(`/file/${fileId}/details`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isDocumentVisible && previousStatusRef.current === 'waiting') {
            backgroundedDuringPollingRef.current = true;
        }
    }, [isDocumentVisible]);

    useEffect(() => {
        console.log('FileDetails - response:', response);
        console.log('FileDetails - response type:', typeof response);
        console.log('FileDetails - error:', error);

        if (!response || typeof response === 'string') {
            console.log('Invalid response, skipping');
            return;
        }

        if (response && response.state === 'success') {
            response.data.formattedDate = moment(response.data.createdAt).format('D MMMM YYYY, HH:mm:ss');

            const currentStatus = response.data.status;

            if (previousStatusRef.current === 'waiting' && currentStatus === 'done') {
                if (!isDocumentVisible || !backgroundedDuringPollingRef.current) {
                    notifyTranscriptionCompleted({
                        fileId: response.data.id,
                        fileName: response.data.name
                    });
                }
            }

            previousStatusRef.current = currentStatus;
            if (currentStatus !== 'waiting') {
                backgroundedDuringPollingRef.current = false;
            }

            if (currentStatus === 'waiting') {
                startPolling();
            } else if (currentStatus === 'done' || currentStatus === 'error') {
                stopPolling();
            }

            if (response.data.transcriptions && response.data.transcriptions.length > 0) {
                setTranscriptions([]);
                response.data.transcriptions.forEach((tr) => {
                    setTranscriptions(prevState => [...prevState, tr]);
                })

            }
            setFileDetails(response.data);
        }
    }, [isDocumentVisible, notifyTranscriptionCompleted, response, error, startPolling, stopPolling])

    useEffect(() => {
        if (error) {
            console.error('Error loading file details:', error);
            navigate('/');
        }
    }, [error, navigate])

    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, []);

    const transcriptionsList = transcriptions.map(tr => {
        return (
            <Transcription
                key={tr.id}
                fileName={fileDetails.name}
                transcription={tr}
                onAnalyzeWithAI={fileDetails?.status === 'done' && fileDetails?.folderId ? analyzeWithAIClickHandler : null}
                onSummaryGenerated={(updatedTranscription) => {
                    setTranscriptions((currentTranscriptions) => currentTranscriptions.map((currentTranscription) => (
                        currentTranscription.id === updatedTranscription.id ? updatedTranscription : currentTranscription
                    )));
                }}
            />
        );
    });

    return <>
        <Stack width="100%" direction="row" justifyContent="flex-end" alignItems="center" mb={1}>
            <Button startIcon={<ChevronLeftIcon />} onClick={() => navigate('/dashboard')}>
                Torna indietro
            </Button>
        </Stack>
        {!fileDetails && <Skeleton variant='rectangular' height={42} ></Skeleton>}
        {fileDetails && <>
            <Stack maxWidth={'100%'} spacing={{ xs: 0.5, sm: 2 }} direction={{ xs: 'column', sm: "row" }} alignItems={{ xs: 'flex-start', sm: 'center' }} >
                <Stack spacing={0.5} sx={{ marginRight: 'auto!important', minWidth: 0 }}>
                    {fileDetails.folderName && (
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                            <FolderSharedOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0 }} />
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                fontWeight="bold"
                                sx={{
                                    maxWidth: { xs: '100%', sm: '400px' },
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {fileDetails.folderName}
                            </Typography>
                        </Stack>
                    )}
                    <Typography variant={isAppView ? 'h7' : 'h6'} fontWeight='bold' sx={{
                        maxWidth: { xs: '100%', sm: '400px' },
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                    }}>{fileDetails.name}</Typography>
                </Stack>
                <Stack alignItems="flex-end" spacing={1}>
                    <Stack direction={{ xs: 'row', sm: "row" }} alignItems="center" spacing={1}>
                        <IconButton
                            size="large"
                            color="primary"
                            onClick={renameBtnClickHandler}
                        >
                            <DriveFileRenameOutlineIcon />
                        </IconButton>
                        <IconButton
                            size="large"
                            color={"error"}
                            onClick={deleteBtnClickHandler}
                        >
                            <DeleteForeverIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </Stack>
        </>}
        <Box my={1}>
            {!fileDetails && <Skeleton variant='rectangular' height={24} ></Skeleton>}
            {fileDetails && <Typography variant='body1'>
                {fileDetails.formattedDate}
            </Typography>}
        </Box>
        <Box my={3}>
            {(!transcriptions.length && !fileDetails) && <Skeleton variant='rectangular' height={150} ></Skeleton>}
            {(!transcriptions.length && fileDetails?.status === 'waiting') && (
                <Typography variant='h6'>Trascrizione in elaborazione...</Typography>
            )}
            {(!transcriptions.length && fileDetails?.status === 'uploaded') && (
                <Typography variant='h6'>File caricato. Avvia la trascrizione per generare il testo.</Typography>
            )}
            {(!transcriptions.length && fileDetails?.status === 'error') && (
                <Alert severity="error">
                    Si e verificato un errore durante la trascrizione del file. Riprova tra poco.
                </Alert>
            )}
            {(transcriptions.length > 0 && fileDetails) && <Stack direction={{ xs: 'column', sm: "row" }} spacing={3}>
                {transcriptionsList}
            </Stack>}
        </Box>
    </>;
}

export default FileDetails;
